import {AuiIf, ThreadPrimitive, useAui} from '@assistant-ui/react';
import {Button, CalloutIcon, DotProgress, Link} from '@wandb/components';
import React, {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';

import {
  useActiveThread,
  useActiveThreadLoadState,
  useIsOnNewChat,
} from '../../agentChat/store/context';
import {useAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {useReloadThread} from '../../agentChat/useReloadThread';
import {AgentChatBanner} from '../AgentChat/banners/AgentChatBanner';
import {useWBAgentBillingBanner} from '../AgentChat/banners/useWBAgentBillingBanner';
import {ErrorBoundary} from '../ErrorBoundary';
import {ACCEPTED_IMAGE_MEDIA_TYPES} from './attachments/imageParts';
import {useComposerImageUpload} from './attachments/useComposerImageUpload';
import {WBAgentComposer} from './composer/WBAgentComposer';
import {AGENT_NAME} from './constants';
import {AgentSuggestionList} from './suggestions/AgentSuggestionList';
import {
  useWBAgentAutoscrollSuppression,
  WBAgentAutoscrollContext,
} from './WBAgentAutoscrollContext';
import {
  WBAgentDebugViewProvider,
  WBAgentDebugViewSlot,
} from './WBAgentDebugView';
import {AssistantMessage} from './WBAgentMessageAssistant';
import {UserMessage} from './WBAgentMessageUser';

const messageComponents = {UserMessage, AssistantMessage};

const ARIA_BLOG_POST_URL =
  'https://wandb.ai/wandb/aria/reports/Introducing-CoreWeave-ARIA-AI-Research-and-Iteration-Agent--VmlldzoxNzM1MzA4Mg';

export const WBAgentThread = () => {
  const {activeThreadId, isLoading, failure} = useActiveThreadLoadState();
  const activeThread = useActiveThread();
  const isOnNewChat = useIsOnNewChat();
  const billingBanner = useWBAgentBillingBanner();

  const aui = useAui();
  const reloadThread = useReloadThread();
  const isReadonly = useAgentChatReadonly();

  const {dismissed: specialWelcomeDismissed, dismiss: dismissSpecialWelcome} =
    useDismissibleNotice('wbagent-special-welcome');
  const [isFirstVisit] = useState(() => !specialWelcomeDismissed);
  useEffect(() => {
    if (isFirstVisit) {
      dismissSpecialWelcome();
    }
  }, [isFirstVisit, dismissSpecialWelcome]);

  // Lets expanding step disclosures temporarily disable AUI's
  // resize-triggered autoscroll
  const {suppressed: autoscrollSuppressed, suppress: suppressAutoscroll} =
    useWBAgentAutoscrollSuppression();

  // Window-wide image drop zone. `noClick`/`noKeyboard` keep the picker from
  // opening on stray clicks; the composer's "Add image" menu triggers `open`.
  // Disabled when the chat is read-only so drops and the picker no-op.
  const {onDrop} = useComposerImageUpload();
  const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_MEDIA_TYPES,
    noClick: true,
    noKeyboard: true,
    disabled: isReadonly,
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <DotProgress aria-label="Loading thread" />
      </div>
    );
  }

  if (failure?.kind === 'error' && activeThreadId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="warning" color="red" size="md" />
        <span className="font-semibold text-base">Failed to load chat</span>
        <Button
          variant="secondary"
          startIcon="reload-refresh"
          onPress={() => void reloadThread(activeThreadId)}>
          Retry
        </Button>
      </div>
    );
  }

  if (failure?.kind === 'not-found') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="search" color="moon" size="md" />
        <span className="font-semibold text-base">Couldn't find this chat</span>
        <Button
          variant="primary"
          startIcon="compose"
          onPress={() => aui.threads().switchToNewThread()}>
          Start a new chat
        </Button>
      </div>
    );
  }

  const emptyThreadError =
    activeThread &&
    activeThread.messageTree.rootChildren.length === 0 &&
    activeThread.error;

  return (
    <WBAgentDebugViewProvider>
      <WBAgentAutoscrollContext.Provider value={suppressAutoscroll}>
        <div
          {...getRootProps()}
          className="relative flex flex-1 min-h-0 flex-col">
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/90 text-xl font-medium text-moon-800 py-16">
              <div className="flex flex-col items-center gap-16">
                <CalloutIcon icon="photo-picture" color="teal" />
                Drop images here to add to chat
              </div>
            </div>
          )}
          <ThreadPrimitive.Root
            className="[container-type:inline-size] flex flex-1 min-h-0 flex-col"
            style={{
              ['--thread-max-width' as string]: '44rem',
            }}>
            <ThreadPrimitive.ViewportProvider>
              <ThreadPrimitive.Viewport
                turnAnchor="bottom"
                autoScroll={!autoscrollSuppressed}
                className="flex flex-1 flex-col overflow-y-auto scroll-smooth overscroll-contain pl-24 pr-24 pt-12 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
                {/* Sticky pins the banner to the thread top on scroll; -mx-24
                    cancels the viewport's px-24 so it spans full width before
                    re-padding. */}
                {billingBanner && (
                  <div className="sticky top-0 z-10 -mx-24 shrink-0 px-24">
                    <div className="mx-auto w-full max-w-[var(--thread-max-width)]">
                      <AgentChatBanner {...billingBanner} />
                    </div>
                  </div>
                )}
                <WBAgentDebugViewSlot>
                  <MessageRecoveryBoundary>
                    {emptyThreadError ? (
                      <EmptyThreadError message={emptyThreadError} />
                    ) : (
                      <AuiIf condition={s => s.thread.isEmpty}>
                        <div className="flex flex-1 flex-col items-start justify-center gap-12 mt-24 mb-24 text-left w-full max-w-[420px] [@container(min-width:699px)]:max-w-[900px] self-center">
                          <div
                            className={
                              isFirstVisit
                                ? 'flex flex-col items-start gap-8'
                                : 'flex items-center gap-8 self-center'
                            }>
                            <span
                              className={`flex flex-wrap gap-x-[0.25em] text-xl font-semibold${isReadonly ? ' text-moon-800/50' : ''}`}>
                              {isFirstVisit ? (
                                <>
                                  <span>Hi, I'm {AGENT_NAME},</span>
                                  <span>your ML research assistant</span>
                                </>
                              ) : (
                                'How can I help you today?'
                              )}
                            </span>
                          </div>
                          {isFirstVisit && (
                            <div className="flex flex-col gap-8">
                              <span className="text-pretty text-moon-650 text-sm [&_a]:!text-sm">
                                I write code, run experiments, visualize your
                                data, and surface patterns, so you can wake up
                                to a better model.{' '}
                                <Link
                                  href={ARIA_BLOG_POST_URL}
                                  target="_blank"
                                  rel="noopener noreferrer">
                                  Learn more
                                </Link>
                              </span>
                            </div>
                          )}
                          {isOnNewChat && <AgentSuggestionList />}
                        </div>
                      </AuiIf>
                    )}
                    <div className="flex flex-col shrink-0">
                      <ThreadPrimitive.Messages
                        components={messageComponents}
                      />
                    </div>
                  </MessageRecoveryBoundary>
                </WBAgentDebugViewSlot>
              </ThreadPrimitive.Viewport>
              <div className="px-24 py-16">
                <WBAgentComposer onStartAddImage={open} />
              </div>
            </ThreadPrimitive.ViewportProvider>
          </ThreadPrimitive.Root>
        </div>
      </WBAgentAutoscrollContext.Provider>
    </WBAgentDebugViewProvider>
  );
};

function EmptyThreadError({message}: {message: string}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16 text-center">
      <CalloutIcon icon="warning" color="moon" size="md" />
      <span className="max-w-[360px] text-sm text-moon-650">{message}</span>
    </div>
  );
}

/**
 * Auto-recovers from AUI tearing crashes during thread switches [1] by
 * remounting children with fresh subscriptions. Allows one recovery per thread
 * switch; a second error re-throws from `renderError` (error boundaries cannot
 * catch their own render errors) so it bubbles to `AgentChatProvider`.
 *
 * [1] https://github.com/assistant-ui/assistant-ui/issues/3652
 */
const MessageRecoveryBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {activeThreadId} = useActiveThreadLoadState();
  const [recoveryKey, setRecoveryKey] = useState(0);
  const recovered = useRef(false);

  // Reset on thread switch — uses the store value (tick 1), which updates
  // before AUI processes the committed switch (tick 2).
  const prevThreadId = useRef(activeThreadId);
  if (prevThreadId.current !== activeThreadId) {
    prevThreadId.current = activeThreadId;
    recovered.current = false;
  }

  const handleError = useCallback(() => {
    if (recovered.current) {
      return;
    }

    recovered.current = true;

    // Wait one frame so AUI's microtask scheduler finishes flushing before
    // remounting children with new subscriptions.
    requestAnimationFrame(() => setRecoveryKey(k => k + 1));
  }, []);

  const renderError = useCallback((error: Error) => {
    if (recovered.current) {
      throw error; // Propagate a second error up to the next boundary.
    }

    return <></>;
  }, []);

  return (
    <ErrorBoundary
      key={recoveryKey}
      silent
      onError={handleError}
      renderError={renderError}>
      {children}
    </ErrorBoundary>
  );
};
